import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchBoard, type EngageJob } from "../../lib/engageBoard";
import { findActiveLine } from "../../lib/line";
import { useUnits } from "../../hooks/useUnits";
import { fmtWeight, rateParts, type UnitSystem } from "../../lib/units";

// /board?tv=1 fullscreen wall — M6. Always shows the "today" window (no
// interactive chips; a TV is a fixed kiosk, not a dashboard someone drives)
// and rotates between panes. Auto-refresh relies on React Query's own
// refetchIntervalInBackground:false (queryObserver only executes the
// interval fetch when focusManager.isFocused(), which itself listens to
// `visibilitychange`) rather than hand-rolled visibility code — same
// pause-when-hidden behaviour, one less thing to get wrong.
const REFRESH_MS = 10_000;
const PANE_MS = 12_000;
const CELEBRATION_MS = 5_000;

// M6 wave-2: line mode adds a THIRD rotating pane, but only while a line is
// actually live (see lib/line.ts's findActiveLine) — a facility running no
// line jobs never sees it, same silent-degrade contract as the rest of the
// line-mode UI. Design rule from the brief, load-bearing here: this pane
// (and every other line-mode surface) shows the ONE shared line_rate_gpm
// number — never a per-station or per-worker rate. The manager-only
// bottleneck view (DashboardPage's LineStationsModal) is the sole place
// per-station rates are allowed to appear.
type Pane = "today" | "jobs" | "line";

function fmtClock(d: Date): string {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function jobMeta(j: EngageJob, system: UnitSystem): string {
  if (j.progress_pct >= 100) return "🏁 done";
  const parts = [
    j.eta_min != null ? `~${j.eta_min} min to finish` : null,
    `${fmtWeight(Math.max(0, j.remaining_g), system, 0)} to go`,
  ].filter((p): p is string => Boolean(p));
  return parts.join(" · ");
}

export function TvBoard() {
  const units = useUnits();
  const [pane, setPane] = useState<Pane>("today");
  const [now, setNow] = useState(() => new Date());
  const [celebrateJob, setCelebrateJob] = useState<EngageJob | null>(null);
  const prevProgressRef = useRef<Map<number, number>>(new Map());
  const celebrationTimerRef = useRef<number | undefined>(undefined);

  const { data } = useQuery({
    queryKey: ["tv-board"],
    queryFn: () => fetchBoard("today", ""),
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const jobIds = (data?.data.jobs ?? []).map((j) => j.id);
  // Scans every open job's line state each poll to find the (at most one,
  // for now) job that qualifies as a live line — see findActiveLine's own
  // comment. `jobIds.join(",")` as the key (not the array itself) keeps the
  // query stable across polls where the SET of open jobs hasn't changed.
  const { data: lineState } = useQuery({
    queryKey: ["tv-line-state", jobIds.join(",")],
    queryFn: () => findActiveLine(jobIds),
    enabled: jobIds.length > 0,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
  });
  const hasLine = Boolean(lineState);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const order: Pane[] = hasLine ? ["today", "line", "jobs"] : ["today", "jobs"];
    const id = window.setInterval(() => {
      setPane((p) => order[(order.indexOf(p) + 1) % order.length] ?? "today");
    }, PANE_MS);
    return () => window.clearInterval(id);
  }, [hasLine]);

  // The line pane can disappear between polls (last staffed worker clocked
  // out) — if the rotation happened to be sitting on it, jump back to Today
  // rather than rendering nothing until the next rotation tick.
  useEffect(() => {
    if (!hasLine && pane === "line") setPane("today");
  }, [hasLine, pane]);

  // Celebration pane-interrupt: a job crossing 100% between two polls.
  // Compared client-side against the previous poll's progress_pct, keyed by
  // job id — a fresh mount (prevProgressRef empty) never celebrates, only a
  // transition seen across two live snapshots does.
  useEffect(() => {
    const jobs = data?.data.jobs ?? [];
    const prev = prevProgressRef.current;
    const justFinished = jobs.find((j) => {
      const before = prev.get(j.id);
      return before !== undefined && before < 100 && j.progress_pct >= 100;
    });
    if (justFinished) {
      setCelebrateJob(justFinished);
      window.clearTimeout(celebrationTimerRef.current);
      celebrationTimerRef.current = window.setTimeout(() => setCelebrateJob(null), CELEBRATION_MS);
    }
    prevProgressRef.current = new Map(jobs.map((j) => [j.id, j.progress_pct]));
  }, [data]);

  useEffect(() => () => window.clearTimeout(celebrationTimerRef.current), []);

  const board = data?.data.board ?? [];
  const top5 = board.slice(0, 5);
  const jobs = data?.data.jobs ?? [];

  return (
    <div className="tv-board" data-testid="tv-board">
      <div className="tv-clock" data-testid="tv-clock">{fmtClock(now)}</div>

      {celebrateJob ? (
        <div className="tv-celebration" data-testid="tv-celebration">
          <div className="tv-celebration-emoji">🎉</div>
          <div className="tv-celebration-text">{celebrateJob.name} — complete!</div>
        </div>
      ) : (
        <>
          {pane === "today" && (
            <div className="tv-pane" data-testid="tv-pane-today">
              <h1 className="tv-title">Today</h1>
              <div className="tv-rows">
                {top5.map((r, i) => (
                  <div className="tv-row" key={r.id} data-testid="tv-board-row">
                    <span className="tv-rank">
                      {i < 3 ? ["🥇", "🥈", "🥉"][i] : (r.percentile_band ?? "—")}
                    </span>
                    <span className="tv-name">
                      {r.name}
                      {r.is_pb_day ? " ✨" : ""}
                    </span>
                    <span className="tv-grams">{fmtWeight(r.total_grams, units, 0)}</span>
                    <span className="tv-qa">QA {r.avg_qa ? r.avg_qa.toFixed(1) : "—"}</span>
                  </div>
                ))}
                {!top5.length && <div className="tv-empty muted">No runs yet today.</div>}
              </div>
            </div>
          )}

          {pane === "line" && lineState && (
            <div className="tv-pane tv-line-pane" data-testid="tv-pane-line">
              <h1 className="tv-title">{lineState.job.name}</h1>
              <div className="tv-line-rate" data-testid="tv-line-rate">
                {units === "metric" ? lineState.line_rate_gpm : rateParts(lineState.line_rate_gpm, units).value}<span className="tv-line-unit">{rateParts(lineState.line_rate_gpm, units).unit}</span>
              </div>
              <div className="tv-line-eta" data-testid="tv-line-eta">
                {lineState.eta_min != null ? `≈${lineState.eta_min} min to go` : "warming up"}
              </div>
              <div className="progress-track tv-progress-track tv-line-progress">
                <div className="progress-fill" style={{ width: `${Math.min(100, lineState.job.progress_pct)}%` }} />
              </div>
              <div
                className={`tv-line-record${lineState.best_hour_g > 0 && lineState.current_hour_g >= lineState.best_hour_g ? " chasing" : ""}`}
                data-testid="tv-line-record"
              >
                room record: {lineState.best_hour_g}g/hr · this hour: {lineState.current_hour_g}g
                {lineState.best_hour_g > 0 && lineState.current_hour_g >= lineState.best_hour_g ? " 🔥" : ""}
              </div>
              <div className="tv-line-stations">
                {lineState.stations.map((s) => (
                  <div className="tv-line-station" key={s.id} data-testid="tv-line-station-row">
                    <span className="tv-line-station-name">{s.name}{s.is_final ? " · weigh-out" : ""}</span>
                    <span className="tv-line-station-workers muted">
                      {s.workers.length ? s.workers.map((w) => w.name).join(", ") : "unstaffed"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pane === "jobs" && (
            <div className="tv-pane" data-testid="tv-pane-jobs">
              <h1 className="tv-title">Jobs</h1>
              <div className="tv-job-list">
                {jobs.map((j) => (
                  <div className="tv-job-row" key={j.id} data-testid="tv-job-row">
                    <div className="tv-job-name">{j.name}</div>
                    <div className="progress-track tv-progress-track">
                      <div
                        className={`progress-fill${j.progress_pct >= 100 ? " done" : j.endgame ? " endgame" : ""}`}
                        style={{ width: `${Math.min(100, j.progress_pct)}%` }}
                      />
                    </div>
                    <div className="tv-job-meta muted">{jobMeta(j, units)}</div>
                  </div>
                ))}
                {!jobs.length && <div className="tv-empty muted">No open jobs.</div>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
